import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import Button from "../../../../common/ekedc-components/button/Button";
import styles from "./AddBeneficiary.module.scss";
import { useSaveBeneficiaryMutation, BeneficiaryItem } from "../../../../service/beneficiary-service";
import { ConsumerType } from "../../../../model/energy-profile";
import { getErrorMessage } from "../../../../utils/error-handler";
import { MeterSelector } from "../../components";
import { RootState } from "../../../../stores/store";
import { useLazyVerifyPostpaidMeterQuery, useLazyVerifyPrepaidMeterQuery } from "../../../../service/vending-service";
import { VerifyMeterNumberPayload } from "../../../../model/charge";
import { MeterValidationResponse } from "../../buy-electricity/buy-electricity.types";

interface AddBeneficiaryFormData {
	meterNumber: string;
}

export const AddBeneficiaryPage: React.FC = () => {
	const navigate = useNavigate();
	const energyProfile = useSelector((state: RootState) => state?.customerEnergyProfile);
	const [saveBeneficiary, { isLoading }] = useSaveBeneficiaryMutation();
	const [verifyPrepaidMeter] = useLazyVerifyPrepaidMeterQuery();
	const [verifyPostpaidMeter] = useLazyVerifyPostpaidMeterQuery();

	const [type, setType] = useState<ConsumerType | null>(null);
	const [meterValidationData, setMeterValidationData] = useState<MeterValidationResponse | null>(null);
	const [meterValidationError, setMeterValidationError] = useState("");
	const [isValidatingMeter, setIsValidatingMeter] = useState(false);
	const [lastValidatedMeter, setLastValidatedMeter] = useState("");

	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm<AddBeneficiaryFormData>({
		mode: "onChange",
		defaultValues: { meterNumber: "" },
	});

	const watchedMeterNumber = watch("meterNumber");

	const handleSelectType = (next: ConsumerType) => {
		if (next === type) return;
		setType(next);
		reset({ meterNumber: "" });
		setMeterValidationData(null);
		setMeterValidationError("");
		setLastValidatedMeter("");
	};

	const handleBeneficiarySelect = (b: BeneficiaryItem) => {
		setType(b.type);
		setMeterValidationData(null);
		setMeterValidationError("");
		setLastValidatedMeter("");
		setValue("meterNumber", b.identifier, { shouldValidate: true });
	};

	const validateMeter = async (meterNumber: string) => {
		if (!type) return;
		setIsValidatingMeter(true);
		setMeterValidationError("");
		try {
			const payload: VerifyMeterNumberPayload = {
				MeterNumber: meterNumber,
				UtilityId: energyProfile.utilityId ?? "",
			};
			const response =
				type === ConsumerType.Prepaid
					? await verifyPrepaidMeter(payload).unwrap()
					: await verifyPostpaidMeter(payload).unwrap();
			setMeterValidationData({ customerName: response.accountName });
		} catch (error: any) {
			setMeterValidationError(getErrorMessage(error?.data) || "Meter number does not exist in the utility");
			setMeterValidationData(null);
		} finally {
			setIsValidatingMeter(false);
		}
	};

	useEffect(() => {
		if (!type) return;
		if (watchedMeterNumber.length < 9) {
			if (meterValidationData || meterValidationError) {
				setMeterValidationData(null);
				setMeterValidationError("");
				setLastValidatedMeter("");
			}
			return;
		}
		if (watchedMeterNumber === lastValidatedMeter) return;

		const timer = setTimeout(() => {
			setLastValidatedMeter(watchedMeterNumber);
			validateMeter(watchedMeterNumber);
		}, 600);

		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchedMeterNumber, type]);

	const onSubmit = async (data: AddBeneficiaryFormData) => {
		if (!type) return;
		try {
			await saveBeneficiary({ identifier: data.meterNumber.trim(), type }).unwrap();
			toast.success("Beneficiary saved successfully");
			navigate(-1);
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? "Failed to save beneficiary");
		}
	};

	return (
		<CustomerPageLayout title="Add Beneficiary">
			<div className={styles.pageContainer}>
				<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
					<div className={styles.cards}>
						<Controller
							name="meterNumber"
							control={control}
							rules={{
								required: "Meter number is required",
								minLength: { value: 9, message: "Meter number must be at least 9 digits" },
								maxLength: { value: 18, message: "Meter number must not exceed 18 digits" },
							}}
							render={({ field }) => (
								<MeterSelector
									value={type}
									onTypeChange={handleSelectType}
									meterValue={field.value}
									onMeterChange={field.onChange}
									onBeneficiarySelect={handleBeneficiarySelect}
									isValidating={isValidatingMeter}
									validationData={meterValidationData}
									validationError={errors.meterNumber?.message || meterValidationError}
									hideSavedBeneficiaries
								/>
							)}
						/>
					</div>

					<footer className={styles.footer}>
						<Button
							type="submit"
							text="Save Beneficiary"
							loading={isLoading}
							loadingText="Saving..."
							disabled={!watchedMeterNumber.trim() || !type}
						/>
					</footer>
				</form>
			</div>
		</CustomerPageLayout>
	);
};
